import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, DollarSign, ShieldCheck, Sparkles, Share2, PenTool } from "lucide-react";

const ROLES = ["client", "team", "admin", "super_admin"];
const COLORS = ["#002FA7", "#FF4F00", "#16A34A", "#F59E0B", "#0EA5E9"];

export default function AdminPanelPage() {
  const { lang } = useI18n();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const availableRoles = isSuperAdmin ? ROLES : ROLES.filter((r) => r !== "super_admin");
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    const [o, s] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/subscriptions"),
    ]);
    setOverview(o.data);
    setSubs(s.data.subscriptions);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (userId, role) => {
    setSavingId(userId);
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      toast.success(lang === "ar" ? "تم تحديث الدور" : "Role updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSavingId(null); }
  };

  if (!overview) return <AppShell><div className="p-12 text-center text-muted-foreground">Loading…</div></AppShell>;
  const c = overview.counts;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "لوحة الإدارة" : "Admin Panel"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {lang === "ar" ? "المستخدمون، الاشتراكات، والأداء التشغيلي في مكان واحد." : "Users, subscriptions, and operational health in one place."}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-kpis">
        {[
          { l: lang === "ar" ? "المستخدمون" : "Total users", v: c.total_users, Icon: Users, testId: "admin-kpi-users" },
          { l: lang === "ar" ? "العملاء" : "Clients", v: c.clients, Icon: Users, testId: "admin-kpi-clients" },
          { l: lang === "ar" ? "MRR" : "MRR (USD)", v: `$${overview.revenue.mrr}`, Icon: DollarSign, testId: "admin-kpi-mrr" },
          { l: lang === "ar" ? "ARR" : "ARR (USD)", v: `$${overview.revenue.arr}`, Icon: DollarSign, testId: "admin-kpi-arr" },
        ].map((k) => {
          const Icon = k.Icon;
          return (
            <div key={k.l} data-testid={k.testId} className="nx-card p-5 relative overflow-hidden">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{k.l}</div>
                  <div className="font-display text-3xl mt-2 tabular-nums">{k.v}</div>
                </div>
                <div className="w-9 h-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-4 h-4" /></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="admin-secondary">
        {[
          { l: lang === "ar" ? "اشتراك نشط" : "Active subs", v: c.active_subscriptions, Icon: ShieldCheck },
          { l: lang === "ar" ? "منشورات" : "Posts", v: c.posts_created, Icon: PenTool },
          { l: lang === "ar" ? "حسابات اجتماعية" : "Social accts", v: c.social_accounts, Icon: Share2 },
          { l: lang === "ar" ? "محادثات AI" : "AI chats", v: c.chat_sessions, Icon: Sparkles },
        ].map((k) => {
          const Icon = k.Icon;
          return (
            <div key={k.l} className="nx-card p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-sm bg-muted flex items-center justify-center"><Icon className="w-4 h-4 text-muted-foreground" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{k.l}</div>
                <div className="font-display text-2xl tabular-nums">{k.v}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="nx-card p-5 lg:col-span-2" data-testid="admin-signup-trend">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "التسجيلات (١٤ يوم)" : "Sign-ups · last 14 days"}</div>
          <div className="h-64"><ResponsiveContainer>
            <LineChart data={overview.signup_trend}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }} />
              <Line type="monotone" dataKey="signups" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer></div>
        </div>

        <div className="nx-card p-5" data-testid="admin-plan-breakdown">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "توزيع الخطط" : "Plan mix"}</div>
          {overview.plan_breakdown.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">{lang === "ar" ? "لا اشتراكات بعد." : "No subscriptions yet."}</div>
          ) : (
            <div className="h-64"><ResponsiveContainer>
              <PieChart>
                <Pie data={overview.plan_breakdown} dataKey="count" nameKey="plan" outerRadius={80} label>
                  {overview.plan_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer></div>
          )}
        </div>

        <div className="nx-card p-5 lg:col-span-3" data-testid="admin-users-table">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "أحدث المستخدمين" : "Recent users"}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="text-start py-2 pe-4">Name</th><th className="text-start py-2 pe-4">Email</th><th className="text-start py-2 pe-4">Role</th><th className="text-start py-2 pe-4">Joined</th></tr>
              </thead>
              <tbody>
                {overview.recent_users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-border/40">
                    <td className="py-2 pe-4">{u.name || "—"}</td>
                    <td className="py-2 pe-4 text-muted-foreground">{u.email}</td>
                    <td className="py-2 pe-4">
                      <select data-testid={`admin-user-role-${u.id}`} value={u.role}
                        disabled={savingId === u.id || !isSuperAdmin || u.id === currentUser?.id}
                        onChange={(e) => changeRole(u.id, e.target.value)}
                        className="bg-background border border-border rounded-sm px-2 py-1 text-xs outline-none focus:border-primary disabled:opacity-60">
                        {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                        {!availableRoles.includes(u.role) && <option value={u.role}>{u.role}</option>}
                      </select>
                    </td>
                    <td className="py-2 pe-4 text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">{lang === "ar" ? "تغيير الأدوار متاح للـ super_admin فقط." : "Role changes require super_admin privileges."}</p>
        </div>

        <div className="nx-card p-5 lg:col-span-3" data-testid="admin-subs-table">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "الاشتراكات" : "Subscriptions"} · {subs.length}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-start py-2 pe-4">Client</th>
                  <th className="text-start py-2 pe-4">Plan</th>
                  <th className="text-start py-2 pe-4">Cycle</th>
                  <th className="text-start py-2 pe-4">Amount</th>
                  <th className="text-start py-2 pe-4">Status</th>
                  <th className="text-start py-2 pe-4">Started</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">—</td></tr>}
                {subs.map((s, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="py-2 pe-4">
                      {s.user ? (<>
                        <div className="font-medium">{s.user.name || s.user.email}</div>
                        <div className="text-xs text-muted-foreground">{s.user.email}</div>
                      </>) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pe-4">{s.plan_name}</td>
                    <td className="py-2 pe-4">{s.billing_cycle}</td>
                    <td className="py-2 pe-4">${s.amount}</td>
                    <td className="py-2 pe-4 text-emerald-500">{s.status}</td>
                    <td className="py-2 pe-4 text-xs text-muted-foreground">{new Date(s.started_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
